/**
 * Custom hook for tracking map center and zoom
 * Used to provide parameters for optimized layer fetching
 */
import { useState, useEffect, useCallback } from 'react';
import { useMapEvents } from 'react-leaflet';

/**
 * Hook to track map center and zoom level
 * @returns {object} - { center, zoom }
 */
export function useMapState() {
  const [center, setCenter] = useState({ lat: null, lon: null });
  const [zoom, setZoom] = useState(18);
  
  const map = useMapEvents({
    moveend: () => {
      const mapCenter = map.getCenter();
      setCenter({ lat: mapCenter.lat, lon: mapCenter.lng });
    },
    zoomend: () => {
      setZoom(map.getZoom());
    },
    // Initial load
    load: () => {
      const mapCenter = map.getCenter();
      setCenter({ lat: mapCenter.lat, lon: mapCenter.lng });
      setZoom(map.getZoom());
    }
  });

  // Also update on initial render if map is already loaded
  useEffect(() => {
    if (map) {
      const mapCenter = map.getCenter();
      setCenter({ lat: mapCenter.lat, lon: mapCenter.lng });
      setZoom(map.getZoom());
    }
  }, [map]);

  return { center, zoom };
}

/**
 * Component that tracks map state and calls a callback
 * @param {function} onMapStateChange - Callback when map state changes
 */
export function MapStateTracker({ onMapStateChange }) {
  const { center, zoom } = useMapState();
  
  useEffect(() => {
    if (onMapStateChange) {
      onMapStateChange({ center, zoom });
    }
  }, [center, zoom, onMapStateChange]);
  
  return null;
}
